/**
 * ALM domain types — mirror cx-alm DTOs.
 *
 * Sources of truth (server-side):
 *   services/cx-alm/.../presentation/dto/GapAnalysisResponse.java
 *   services/cx-alm/.../presentation/dto/TriggerCalculationRequest.java
 *   services/cx-alm/.../domain/model/GapAnalysis.java
 *
 * BigDecimal values arrive as decimal strings — never coerce to JS Number
 * for arithmetic. Format only at render time.
 */

export type GapTriggerType = 'SCHEDULED' | 'ON_DEMAND' | 'SCENARIO';

/** Matches `GapAnalysisResponse.from(GapAnalysis)`. */
export interface GapAnalysisResponse {
  resultId: string;            // UUID
  tenantId: string;
  calculationDate: string;     // ISO date — yyyy-MM-dd
  engineVersion: string;
  triggerType: GapTriggerType;
  totalNetGap: string;         // BigDecimal as string
  hasLiquidityShortfall: boolean;
}

/** Matches `TriggerCalculationRequest`. */
export interface TriggerCalculationRequest {
  calculationDate: string;     // ISO date — yyyy-MM-dd
  force?: boolean;
}

/**
 * Detailed bucket grid — not yet exposed by AlmController.getLatestGap (returns
 * data:null today). Defined here so the page is ready when the endpoint lands.
 *
 * Mirrors `GapBucket` in the domain plus liquidity ratios.
 */
export interface GapBucket {
  bucketIndex: number;
  label: string;               // e.g. "0-1M", "1-3M"
  assetsInflow: string;        // BigDecimal string
  liabilitiesOutflow: string;
  cumulativeGap: string;
  /** Derived on the server (assetsInflow − liabilitiesOutflow). */
  netGap: string;
}

export interface LiquidityRatios {
  lcrRatio: string;            // ≥ 1.00 → compliant
  nsfrRatio: string;
  lcrHqla: string;
  lcrNetOutflows: string;
}

export interface GapAnalysisDetail extends GapAnalysisResponse {
  /** Flow (tombée) bucket view — cashflows occurring in each bucket. */
  buckets: GapBucket[];
  /**
   * Phase 2.1.1 — Principal (encours) bucket view: outstanding balance at
   * end of each bucket. Same calculation lineage as `buckets`; UI toggle
   * picks which to render. May be empty for results pre-V110.
   */
  principalBuckets?: GapBucket[];
  /** Phase 2.2 — bucket-fold the server applied (echoed back). */
  bucketView?: BucketView;
  liquidityRatios?: LiquidityRatios;
}

/** Phase 2.1.1 — gap presentation modes per spec v3 §6.1. */
export type PresentationMode = 'FLOW' | 'PRINCIPAL';

/**
 * Phase 2.2 — synthetic bucket views.
 * STANDARD          — atomic 7-bucket grid (default, back-compat)
 * SHORT_MEDIUM_LONG — 3 coarse bands (0-3M / 3M-1Y / 1Y+) for execs
 */
export type BucketView = 'STANDARD' | 'SHORT_MEDIUM_LONG';

/**
 * Phase 2.2.B — ephemeral cashflow-dimension filters on Gap.
 * Any subset of fields can be set; empty values disable that dimension.
 */
export interface CashflowFilters {
  currency?: string;       // 3-letter ISO (MAD, EUR, USD)
  instrumentType?: string; // Bond / Loan / Deposit / …
  cfType?: 'INFLOW' | 'OUTFLOW';
  instrument?: string;     // contains-match on instrumentId
}

export function isFilterActive(f: CashflowFilters): boolean {
  return Boolean(f.currency || f.instrumentType || f.cfType || f.instrument);
}
